import { useMemo, useState } from "react";
import { Minus, Plus, Trash2, ShoppingBag, X, MessageCircle, Copy, Check, ArrowRight } from "lucide-react";
import { SIZE_LABELS, RESTAURANT, BAIRROS } from "@/data/menu";
import { toast } from "sonner";

const formatBRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const buildLineName = (c) => (c.isHalfHalf ? `1/2 ${c.name.replace(" / ", " · 1/2 ")}` : c.name);

const buildWhatsAppMessage = ({ cart, form, subtotal, delivery, total, bairro }) => {
  const L = "━━━━━━━━━━━━━━━━━━━━";
  const lines = [];
  lines.push(L);
  lines.push("*DONA MARIA PIZZARIA*");
  lines.push("      NOVO PEDIDO");
  lines.push(L);
  lines.push("");
  lines.push("*CLIENTE*");
  lines.push(`Nome: ${form.name.trim()}`);
  if (form.phone.trim()) lines.push(`Telefone: ${form.phone.trim()}`);
  lines.push("");
  lines.push("*ENDEREÇO DE ENTREGA*");
  lines.push(`${form.street.trim()}, ${form.number.trim()}`);
  if (form.complement.trim()) lines.push(`Complemento: ${form.complement.trim()}`);
  lines.push(`Bairro: ${bairro.label}`);
  if (form.reference.trim()) lines.push(`Ref.: ${form.reference.trim()}`);
  lines.push("");
  lines.push("*ITENS DO PEDIDO*");
  lines.push(L);
  cart.forEach((c) => {
    const sizeTxt = c.size ? ` (${SIZE_LABELS[c.size]})` : "";
    lines.push(`${c.qty}x ${buildLineName(c)}${sizeTxt}`);
    if (c.borda) lines.push(`   Borda: ${c.borda.label} (+${formatBRL(c.borda.price)})`);
    lines.push(`   ${formatBRL(c.price)} un.  =  *${formatBRL(c.price * c.qty)}*`);
  });
  lines.push(L);
  lines.push(`Subtotal: ${formatBRL(subtotal)}`);
  lines.push(`Taxa de entrega (${bairro.label}): ${formatBRL(delivery)}`);
  lines.push(`*TOTAL: ${formatBRL(total)}*`);
  lines.push("");
  lines.push("*PAGAMENTO*");
  lines.push(`Forma: ${form.payment}`);
  if (form.payment === "Pix") {
    lines.push(`Chave PIX: ${RESTAURANT.pix.key}`);
    lines.push("Enviarei o comprovante em seguida.");
  }
  if (form.payment === "Dinheiro") {
    if (form.needsChange && form.changeFor) {
      const changeVal = parseFloat(String(form.changeFor).replace(",", "."));
      lines.push(`Troco para: ${formatBRL(changeVal)}`);
      if (!Number.isNaN(changeVal) && changeVal >= total) {
        lines.push(`Levar troco: ${formatBRL(changeVal - total)}`);
      }
    } else {
      lines.push("Não precisa de troco");
    }
  }
  lines.push(L);
  lines.push("Pedido enviado pelo site.");
  return lines.join("\n");
};

const initialForm = {
  name: "",
  phone: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: BAIRROS[0].id,
  reference: "",
  payment: "Pix",
  needsChange: false,
  changeFor: "",
};

export const CartDrawer = ({ open, onClose, cart, updateQty, removeItem }) => {
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState("items"); // items | checkout | pix
  const [copied, setCopied] = useState(false);

  const subtotal = useMemo(() => cart.reduce((s, c) => s + c.price * c.qty, 0), [cart]);
  const itemCount = cart.reduce((s, c) => s + c.qty, 0);
  const bairro = BAIRROS.find((b) => b.id === form.neighborhood) || BAIRROS[0];
  const delivery = bairro.fee;
  const total = subtotal + delivery;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) return "Informe seu nome completo.";
    if (!form.street.trim()) return "Informe a rua.";
    if (!form.number.trim()) return "Informe o número.";
    if (!form.neighborhood) return "Escolha o bairro.";
    if (!form.payment) return "Escolha a forma de pagamento.";
    if (form.payment === "Dinheiro" && form.needsChange && !form.changeFor.trim())
      return "Informe o valor para troco.";
    return null;
  };

  const goNext = () => {
    const err = validate();
    if (err) return toast.error(err);
    if (cart.length === 0) return toast.error("Sua sacola está vazia.");
    if (form.payment === "Pix") {
      setStep("pix");
    } else {
      sendWhatsApp();
    }
  };

  const sendWhatsApp = () => {
    const msg = buildWhatsAppMessage({ cart, form, subtotal, delivery, total, bairro });
    const url = `https://wa.me/${RESTAURANT.whatsapp}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Abrindo o WhatsApp com seu pedido!");
  };

  const copyPix = async () => {
    try {
      await navigator.clipboard.writeText(RESTAURANT.pix.key);
      setCopied(true);
      toast.success("Chave PIX copiada!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar. Copie manualmente.");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="cart-backdrop" />

      <div className="relative w-full max-w-md mx-auto bg-zinc-950 border-t border-zinc-800 rounded-t-3xl max-h-[92vh] flex flex-col shadow-2xl animate-fade-up">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-rose-500" />
            <h3 className="font-display text-xl">
              {step === "items" ? "Sua Sacola" : step === "checkout" ? "Entrega" : "Pagamento via PIX"}
            </h3>
            {itemCount > 0 && step === "items" && (
              <span className="ml-1 text-[11px] font-semibold text-zinc-400">
                {itemCount} {itemCount === 1 ? "item" : "itens"}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="cart-close-btn"
            className="w-9 h-9 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {cart.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingBag className="w-10 h-10 mx-auto text-zinc-700" />
              <p className="mt-4 text-zinc-400 text-sm">Sua sacola está vazia.</p>
            </div>
          ) : step === "items" ? (
            <ul className="space-y-3" data-testid="cart-items-list">
              {cart.map((c) => (
                <li key={c.id} className="flex gap-3 items-start bg-zinc-900/60 border border-zinc-800 rounded-2xl p-3" data-testid={`cart-line-${c.id}`}>
                  <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-zinc-800">
                    {c.image && <img src={c.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    {c.isHalfHalf && (
                      <span className="inline-block text-[9px] font-bold uppercase tracking-widest text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-full px-1.5 py-0.5 mb-1">
                        Meio a Meio
                      </span>
                    )}
                    <p className="text-sm font-semibold text-zinc-100 truncate">{c.name}</p>
                    {c.size && (
                      <p className="text-[11px] uppercase tracking-widest text-zinc-500">{SIZE_LABELS[c.size]}</p>
                    )}
                    {c.borda && (
                      <p className="text-[11px] text-rose-300 mt-0.5">Borda: {c.borda.label}</p>
                    )}
                    <p className="text-rose-500 text-sm font-bold mt-1">{formatBRL(c.price * c.qty)}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-full px-1">
                      <button onClick={() => updateQty(c.id, c.qty - 1)} data-testid={`cart-dec-${c.id}`} className="w-7 h-7 flex items-center justify-center">
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-xs font-bold w-4 text-center" data-testid={`cart-qty-${c.id}`}>{c.qty}</span>
                      <button onClick={() => updateQty(c.id, c.qty + 1)} data-testid={`cart-inc-${c.id}`} className="w-7 h-7 flex items-center justify-center">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <button onClick={() => removeItem(c.id)} data-testid={`cart-remove-${c.id}`} className="text-[11px] text-zinc-500 flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> remover
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : step === "checkout" ? (
            <div className="space-y-4" data-testid="checkout-form">
              <FormField label="Nome completo *">
                <input data-testid="checkout-name" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Ex: Maria da Silva" className={inputCls} />
              </FormField>
              <FormField label="Telefone (opcional)">
                <input data-testid="checkout-phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="(43) 99999-9999" className={inputCls} />
              </FormField>
              <div className="grid grid-cols-3 gap-2">
                <FormField label="Rua *" className="col-span-2">
                  <input data-testid="checkout-street" value={form.street} onChange={(e) => setField("street", e.target.value)} placeholder="Nome da rua" className={inputCls} />
                </FormField>
                <FormField label="Nº *">
                  <input data-testid="checkout-number" value={form.number} onChange={(e) => setField("number", e.target.value)} placeholder="123" className={inputCls} />
                </FormField>
              </div>
              <FormField label="Complemento">
                <input data-testid="checkout-complement" value={form.complement} onChange={(e) => setField("complement", e.target.value)} placeholder="Apto, bloco, casa..." className={inputCls} />
              </FormField>
              <FormField label="Bairro *">
                <select
                  data-testid="checkout-neighborhood"
                  value={form.neighborhood}
                  onChange={(e) => setField("neighborhood", e.target.value)}
                  className={inputCls + " appearance-none"}
                >
                  {BAIRROS.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.label} — {formatBRL(b.fee)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Referência (opcional)">
                <input data-testid="checkout-reference" value={form.reference} onChange={(e) => setField("reference", e.target.value)} placeholder="Perto de..." className={inputCls} />
              </FormField>

              <div>
                <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">
                  Forma de pagamento *
                </label>
                <div className="mt-2 grid grid-cols-3 gap-2" data-testid="payment-options">
                  {["Pix", "Cartão na Entrega", "Dinheiro"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`payment-${p.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      onClick={() => setField("payment", p)}
                      className={`press text-[12px] font-semibold py-2.5 rounded-xl border ${
                        form.payment === p ? "bg-rose-600 border-rose-600 text-white" : "bg-zinc-900 border-zinc-800 text-zinc-300"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {form.payment === "Dinheiro" && (
                <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-3 space-y-3">
                  <label className="flex items-center gap-2 text-sm text-zinc-200">
                    <input type="checkbox" data-testid="needs-change" checked={form.needsChange} onChange={(e) => setField("needsChange", e.target.checked)} className="accent-rose-600 w-4 h-4" />
                    Preciso de troco
                  </label>
                  {form.needsChange && (
                    <FormField label="Troco para quanto? *">
                      <input data-testid="checkout-change-for" inputMode="decimal" value={form.changeFor} onChange={(e) => setField("changeFor", e.target.value)} placeholder="Ex: 100,00" className={inputCls} />
                    </FormField>
                  )}
                </div>
              )}

              {/* Cost breakdown */}
              <div className="rounded-xl bg-zinc-900/60 border border-zinc-800 p-3 text-sm space-y-1.5" data-testid="cost-breakdown">
                <Row label="Subtotal" value={formatBRL(subtotal)} />
                <Row label={`Entrega (${bairro.label})`} value={formatBRL(delivery)} />
                <div className="border-t border-zinc-800 my-1" />
                <Row label={<span className="text-zinc-100 font-semibold">Total</span>} value={<span className="text-rose-500 font-display text-lg">{formatBRL(total)}</span>} />
              </div>
            </div>
          ) : (
            /* PIX step */
            <div className="space-y-4" data-testid="pix-screen">
              <div className="rounded-2xl border border-rose-600/30 bg-gradient-to-b from-rose-600/10 to-transparent p-5 text-center">
                <p className="text-[11px] uppercase tracking-widest text-rose-400 font-semibold">Valor a pagar</p>
                <p className="font-display text-4xl text-white mt-1" data-testid="pix-total">{formatBRL(total)}</p>
                <p className="text-[11px] text-zinc-500 mt-1">Já incluso a taxa de entrega de {formatBRL(delivery)}</p>
              </div>

              <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4">
                <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Chave PIX ({RESTAURANT.pix.keyType})</p>
                <div className="mt-2 flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-xl px-3 py-3">
                  <span className="flex-1 text-sm font-mono text-zinc-100 truncate" data-testid="pix-key">
                    {RESTAURANT.pix.key}
                  </span>
                  <button
                    onClick={copyPix}
                    data-testid="pix-copy-btn"
                    className={`press flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg ${
                      copied ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"
                    }`}
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copiado" : "Copiar"}
                  </button>
                </div>
                <p className="text-[11px] text-zinc-500 mt-2">Recebedor: {RESTAURANT.pix.holder}</p>
              </div>

              <ol className="space-y-2 text-sm text-zinc-300 list-decimal list-inside">
                <li>Abra o app do seu banco e escolha <span className="text-rose-400 font-semibold">PIX → Pagar com chave</span>.</li>
                <li>Cole a chave e confirme o valor de <span className="text-rose-400 font-semibold">{formatBRL(total)}</span>.</li>
                <li>Guarde o <span className="text-rose-400 font-semibold">comprovante</span> — você vai anexar no WhatsApp para confirmarmos o pedido.</li>
              </ol>

              <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 text-[12px] text-amber-200">
                ⚠️ Seu pedido só entra na fila do forno após confirmarmos o pagamento pelo WhatsApp.
              </div>
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-zinc-800 bg-zinc-950 p-4 space-y-3">
            {step === "items" && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-zinc-500">Subtotal</span>
                  <span className="font-display text-2xl text-rose-500" data-testid="cart-total">{formatBRL(subtotal)}</span>
                </div>
                <button data-testid="go-checkout-btn" onClick={() => setStep("checkout")} className="press w-full bg-gradient-to-b from-rose-500 to-rose-600 text-white font-semibold py-3.5 rounded-2xl shadow-[0_10px_30px_-10px_rgba(244,63,94,0.7)]">
                  Ir para entrega
                </button>
              </>
            )}
            {step === "checkout" && (
              <div className="grid grid-cols-4 gap-2">
                <button data-testid="back-to-items-btn" onClick={() => setStep("items")} className="press col-span-1 bg-zinc-900 border border-zinc-800 text-zinc-300 font-semibold py-3 rounded-2xl text-sm">
                  Voltar
                </button>
                <button
                  data-testid={form.payment === "Pix" ? "go-pix-btn" : "whatsapp-submit-btn"}
                  onClick={goNext}
                  className="press col-span-3 flex items-center justify-center gap-2 bg-gradient-to-b from-rose-500 to-rose-600 text-white font-semibold py-3 rounded-2xl"
                >
                  {form.payment === "Pix" ? (
                    <>
                      Continuar para PIX
                      <ArrowRight className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      <MessageCircle className="w-4 h-4" />
                      Enviar Pedido via WhatsApp
                    </>
                  )}
                </button>
              </div>
            )}
            {step === "pix" && (
              <div className="grid grid-cols-4 gap-2">
                <button data-testid="back-to-checkout-btn" onClick={() => setStep("checkout")} className="press col-span-1 bg-zinc-900 border border-zinc-800 text-zinc-300 font-semibold py-3 rounded-2xl text-sm">
                  Voltar
                </button>
                <button
                  data-testid="whatsapp-submit-btn"
                  onClick={sendWhatsApp}
                  className="press col-span-3 flex items-center justify-center gap-2 bg-gradient-to-b from-emerald-500 to-emerald-600 text-white font-semibold py-3 rounded-2xl shadow-[0_10px_30px_-10px_rgba(16,185,129,0.6)]"
                >
                  <MessageCircle className="w-4 h-4" />
                  Enviar comprovante via WhatsApp
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const inputCls =
  "w-full bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-rose-600 focus:ring-2 focus:ring-rose-600/20";

const FormField = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-zinc-400 text-xs">{label}</span>
    <span className="text-zinc-100 text-sm">{value}</span>
  </div>
);
